import { useEffect, useState, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { Database, LanguageLevel } from '../types/database'
import { CardForm } from './CardForm'
import { BatchAdd } from './BatchAdd'
import { DataLayer } from '../lib/dataLayer'

type Card = Database['public']['Tables']['cards']['Row']

interface ExerciseInfo {
  id: string
  name: string
}

export function CardListView() {
  const { user, isAdmin } = useAuth()
  const [cards, setCards] = useState<Card[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedCards, setSelectedCards] = useState<Set<string>>(new Set())

  // Filter states
  const [searchText, setSearchText] = useState('')
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [availableTags, setAvailableTags] = useState<string[]>([])
  const [selectedExercises, setSelectedExercises] = useState<string[]>([])
  const [availableExercises, setAvailableExercises] = useState<ExerciseInfo[]>([])
  const [exerciseNameMap, setExerciseNameMap] = useState<Map<string, string>>(new Map())
  
  // JSON edit mode
  const [isEditingJson, setIsEditingJson] = useState(false)
  const [jsonText, setJsonText] = useState('')
  const [jsonError, setJsonError] = useState<string | null>(null)
  
  // Tag editing
  const [editingTagCardId, setEditingTagCardId] = useState<string | null>(null)
  const [newTagInput, setNewTagInput] = useState('')
  const [isBulkAddingTag, setIsBulkAddingTag] = useState(false)
  const [bulkTagInput, setBulkTagInput] = useState('')
  
  // Language editing
  const [bulkLanguage, setBulkLanguage] = useState<string | null>(null)
  
  // Create/Edit mode
  const [isCreating, setIsCreating] = useState(false)
  const [editingCard, setEditingCard] = useState<Card | null>(null)
  const [showBatchAdd, setShowBatchAdd] = useState(false)
  
  // JSON field selection
  const [selectedJsonFields, setSelectedJsonFields] = useState<Set<string>>(
    new Set(['front', 'back']) // Default to front and back
  )
  const [showEditJsonDropdown, setShowEditJsonDropdown] = useState(false)
  const [showExportJsonDropdown, setShowExportJsonDropdown] = useState(false)
  const availableFields = ['id', 'front', 'back', 'language', 'tags', 'level']

  const loadCards = async () => {
    try {
      setLoading(true)
      const cards = await DataLayer.getCards()
      
      // Sort by created_at descending
      cards.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      
      setCards(cards)

      // Load exercises and build lookup map
      const exercises = await DataLayer.getExercises()
      const exMap = new Map(exercises.map(e => [e.id, e.name]))
      setExerciseNameMap(exMap)

      // Extract unique tags and exercise IDs
      const allTags = new Set<string>()
      const exerciseIds = new Set<string>()
      cards.forEach(card => {
        if (card.exercise_id) exerciseIds.add(card.exercise_id)
        card.tags.forEach((tag: string) => allTags.add(tag))
      })
      setAvailableTags(Array.from(allTags).sort())
      setAvailableExercises(
        Array.from(exerciseIds)
          .map(id => ({ id, name: exMap.get(id) || id }))
          .sort((a, b) => a.name.localeCompare(b.name))
      )
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (user) {
      loadCards()
    }
  }, [user])

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (!target.closest('.json-dropdown-container')) {
        setShowEditJsonDropdown(false)
        setShowExportJsonDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Filter cards based on search, exercises, and tags
  const filteredCards = useMemo(() => {
    return cards.filter(card => {
      // Text search
      if (searchText) {
        const searchLower = searchText.toLowerCase()
        if (!card.front.toLowerCase().includes(searchLower) && 
            !card.back.toLowerCase().includes(searchLower)) {
          return false
        }
      }
      
      // Exercise filter (by exercise_id)
      if (selectedExercises.length > 0) {
        if (!card.exercise_id || !selectedExercises.includes(card.exercise_id)) {
          return false
        }
      }
      
      // Tag filter
      if (selectedTags.length > 0) {
        if (!selectedTags.some(tag => card.tags.includes(tag))) {
          return false
        }
      }
      
      return true
    })
  }, [cards, searchText, selectedTags, selectedExercises])

  const handleSelectAll = () => {
    if (selectedCards.size === filteredCards.length) {
      setSelectedCards(new Set())
    } else {
      setSelectedCards(new Set(filteredCards.map(card => card.id)))
    }
  }

  const handleSelectCard = (cardId: string) => {
    const newSelected = new Set(selectedCards)
    if (newSelected.has(cardId)) {
      newSelected.delete(cardId)
    } else {
      newSelected.add(cardId)
    }
    setSelectedCards(newSelected)
  }

  const toggleFieldSelection = (field: string) => {
    const newFields = new Set(selectedJsonFields)
    if (newFields.has(field)) {
      newFields.delete(field)
    } else {
      newFields.add(field)
    }
    setSelectedJsonFields(newFields)
  }

  const handleDeleteSelected = async () => {
    if (selectedCards.size === 0) return
    
    if (!window.confirm(`Are you sure you want to delete ${selectedCards.size} card(s)?`)) return

    try {
      // Delete each selected card using DataLayer
      for (const cardId of Array.from(selectedCards)) {
        await DataLayer.deleteCard(cardId)
      }
      
      // Remove deleted cards from state
      setCards(cards.filter(card => !selectedCards.has(card.id)))
      setSelectedCards(new Set())
    } catch (err: any) {
      setError(err.message)
    }
  }

  const handleResetSelected = async () => {
    if (selectedCards.size === 0) return
    
    if (!window.confirm(`Reset progress for ${selectedCards.size} card(s)? This will set mastery to 0 and unhide them.`)) return

    try {
      // Delete existing progress records for selected cards
      const { error } = await supabase
        .from('user_card_progress')
        .delete()
        .in('card_id', Array.from(selectedCards))
        .eq('user_id', user!.id)

      if (error) throw error
      
      setSelectedCards(new Set())
      // Show success message
      setError(null)
      alert(`Reset progress for ${selectedCards.size} card(s)`)
    } catch (err: any) {
      setError(err.message)
    }
  }

  const handleExportSelected = () => {
    const selectedCardData = filteredCards
      .filter(card => selectedCards.has(card.id))
      .map(card => {
        const exportCard: any = {}
        if (selectedJsonFields.has('id')) exportCard.id = card.id
        if (selectedJsonFields.has('front')) exportCard.front = card.front
        if (selectedJsonFields.has('back')) exportCard.back = card.back
        if (selectedJsonFields.has('language')) exportCard.language = card.language
        if (selectedJsonFields.has('tags')) exportCard.tags = card.tags
        if (selectedJsonFields.has('level')) exportCard.level = card.level
        return exportCard
      })
    
    const json = JSON.stringify(selectedCardData, null, 2)
    
    // Create a blob and download
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'flashcards.json'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const handleEditJson = () => {
    const selectedCardData = filteredCards
      .filter(card => selectedCards.has(card.id))
      .map(card => {
        const exportCard: any = {}
        if (selectedJsonFields.has('id')) exportCard.id = card.id
        if (selectedJsonFields.has('front')) exportCard.front = card.front
        if (selectedJsonFields.has('back')) exportCard.back = card.back
        if (selectedJsonFields.has('language')) exportCard.language = card.language
        if (selectedJsonFields.has('tags')) exportCard.tags = card.tags
        if (selectedJsonFields.has('level')) exportCard.level = card.level
        return exportCard
      })
    
    const json = JSON.stringify(selectedCardData, null, 2)
    setJsonText(json)
    setIsEditingJson(true)
    setJsonError(null)
  }

  const handleSaveJson = async () => {
    try {
      setJsonError(null)
      
      // Parse the JSON
      let editedCards: Array<{id: string, front: string, back: string, language?: string, tags?: string[], level?: LanguageLevel}>
      try {
        editedCards = JSON.parse(jsonText)
      } catch (err) {
        throw new Error('Invalid JSON format')
      }

      // Validate the structure
      if (!Array.isArray(editedCards)) {
        throw new Error('JSON must be an array of cards')
      }

      // Create a map of original cards for reference
      const originalCards = new Map(
        filteredCards
          .filter(card => selectedCards.has(card.id))
          .map(card => [card.id, card])
      )

      // Validate and prepare updates
      const updates = editedCards.map((editedCard, index) => {
        if (!editedCard.id || !editedCard.front || !editedCard.back) {
          throw new Error(`Card ${index + 1} is missing required fields (id, front, back)`)
        }

        const originalCard = originalCards.get(editedCard.id)
        if (!originalCard) {
          throw new Error(`Card with id ${editedCard.id} not found in selected cards`)
        }

        return {
          ...originalCard,
          id: editedCard.id,
          front: editedCard.front,
          back: editedCard.back,
          language: editedCard.language || originalCard.language,
          tags: editedCard.tags || originalCard.tags,
          level: editedCard.level || originalCard.level,
          updated_at: new Date().toISOString()
        }
      })

      // Update cards in database using DataLayer for offline-first support
      for (const update of updates) {
        await DataLayer.updateCard(update.id, {
          front: update.front,
          back: update.back,
          language: update.language,
          tags: update.tags,
          level: update.level
        }, user!.id)
      }

      // Update local state
      const updatedCardIds = new Set(updates.map(u => u.id))
      setCards(cards.map(card => {
        if (updatedCardIds.has(card.id)) {
          const update = updates.find(u => u.id === card.id)!
          return { ...card, ...update }
        }
        return card
      }))

      // Exit edit mode
      setIsEditingJson(false)
      setJsonText('')
      setSelectedCards(new Set())

    } catch (err: any) {
      setJsonError(err.message)
    }
  }

  const handleCancelJsonEdit = () => {
    setIsEditingJson(false)
    setJsonText('')
    setJsonError(null)
  }

  const handleTagToggle = (tag: string) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag))
    } else {
      setSelectedTags([...selectedTags, tag])
    }
  }

  const handleExerciseToggle = (exerciseId: string) => {
    if (selectedExercises.includes(exerciseId)) {
      setSelectedExercises(selectedExercises.filter(e => e !== exerciseId))
    } else {
      setSelectedExercises([...selectedExercises, exerciseId])
    }
  }

  const handleRemoveTag = async (cardId: string, tagToRemove: string) => {
    try {
      const card = cards.find(c => c.id === cardId)
      if (!card) return

      const newTags = card.tags.filter(tag => tag !== tagToRemove)
      
      await DataLayer.updateCard(cardId, { tags: newTags }, user!.id)

      // Update local state immediately
      setCards(cards.map(c => 
        c.id === cardId ? { ...c, tags: newTags } : c
      ))
      
    } catch (err: any) {
      setError(err.message)
    }
  }

  const handleAddTag = async (cardId: string) => {
    if (!newTagInput.trim()) return

    const tagToAdd = newTagInput.trim()

    try {
      const card = cards.find(c => c.id === cardId)
      if (!card) return

      // Only add if not already present
      if (card.tags.includes(tagToAdd)) {
        setNewTagInput('')
        setEditingTagCardId(null)
        return
      }

      const newTags = [...card.tags, tagToAdd]
      
      await DataLayer.updateCard(cardId, { tags: newTags }, user!.id)

      // Update local state immediately
      setCards(cards.map(c => 
        c.id === cardId ? { ...c, tags: newTags } : c
      ))

      // Reset input
      setNewTagInput('')
      setEditingTagCardId(null)
      
    } catch (err: any) {
      setError(err.message)
    }
  }

  const handleBulkRemoveTag = async (tagToRemove: string) => {
    if (selectedCards.size === 0) return

    try {
      // Update each selected card
      for (const cardId of Array.from(selectedCards)) {
        const card = cards.find(c => c.id === cardId)
        if (!card) continue

        const newTags = card.tags.filter(tag => tag !== tagToRemove)
        
        await DataLayer.updateCard(cardId, { tags: newTags }, user!.id)
      }

      // Update local state
      setCards(cards.map(card => {
        if (selectedCards.has(card.id)) {
          return { ...card, tags: card.tags.filter(tag => tag !== tagToRemove) }
        }
        return card
      }))
      
    } catch (err: any) {
      setError(err.message)
    }
  }

  const handleBulkAddTag = async () => {
    if (selectedCards.size === 0 || !bulkTagInput.trim()) return

    const tagToAdd = bulkTagInput.trim()

    try {
      // Update each selected card
      for (const cardId of Array.from(selectedCards)) {
        const card = cards.find(c => c.id === cardId)
        if (!card) continue

        // Only add if not already present
        if (card.tags.includes(tagToAdd)) continue

        const newTags = [...card.tags, tagToAdd]
        
        await DataLayer.updateCard(cardId, { tags: newTags }, user!.id)
      }

      // Update local state
      setCards(cards.map(card => {
        if (selectedCards.has(card.id) && !card.tags.includes(tagToAdd)) {
          return { ...card, tags: [...card.tags, tagToAdd] }
        }
        return card
      }))

      // Reset input
      setBulkTagInput('')
      setIsBulkAddingTag(false)
      
    } catch (err: any) {
      setError(err.message)
    }
  }

  const handleBulkLanguageUpdate = async (language: string) => {
    if (selectedCards.size === 0) return

    try {
      // Update each selected card
      for (const cardId of Array.from(selectedCards)) {
        await DataLayer.updateCard(cardId, { language }, user!.id)
      }

      // Update local state
      setCards(cards.map(card => {
        if (selectedCards.has(card.id)) {
          return { ...card, language }
        }
        return card
      }))

      setBulkLanguage(null)
      
    } catch (err: any) {
      setError(err.message)
    }
  }

  const handleEdit = (card: Card) => {
    setEditingCard(card)
    setIsCreating(true)
  }

  const handleClone = async (card: Card) => {
    try {
      const clonedCard = await DataLayer.cloneCard(card.id, user!.id)
      if (clonedCard) {
        // Open the cloned card in edit mode
        setEditingCard(clonedCard)
        setIsCreating(true)
      }
    } catch (err: any) {
      setError(`Failed to clone card: ${err.message}`)
    }
  }

  const handleCreateSuccess = () => {
    setIsCreating(false)
    setEditingCard(null)
    loadCards() // Refresh the card list
  }

  const handleBatchAddSuccess = () => {
    setShowBatchAdd(false)
    loadCards() // Refresh the card list
  }

  const handleForceResync = async () => {
    if (!window.confirm('This will force all users to clear their local data and resync. Are you sure?')) {
      return
    }

    try {
      const { error } = await supabase
        .from('system_config')
        .update({ 
          value: {
            timestamp: new Date().toISOString(),
            reason: 'Admin forced resync'
          }
        })
        .eq('key', 'force_resync')

      if (error) throw error
      alert('Force resync triggered. All users will need to reload their data.')
    } catch (err: any) {
      setError(`Failed to trigger force resync: ${err.message}`)
    }
  }

  if (loading) {
    return <div className="text-center py-4">Loading cards...</div>
  }

  if (error) {
    return (
      <div className="rounded-md bg-red-50 p-4">
        <p className="text-sm text-red-800">{error}</p>
      </div>
    )
  }

  if (showBatchAdd) {
    return (
      <BatchAdd 
        onSuccess={handleBatchAddSuccess}
        onCancel={() => setShowBatchAdd(false)}
      />
    )
  }

  if (isCreating) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-medium text-gray-900">
                {editingCard ? 'Edit Card' : 'Create New Card'}
              </h2>
              <button
                onClick={() => {
                  setIsCreating(false)
                  setEditingCard(null)
                }}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>
            <CardForm
              onSuccess={handleCreateSuccess}
              initialData={editingCard || undefined}
            />
          </div>
        </div>
      </div>
    )
  }

  if (isEditingJson) {
    return (
      <div className="space-y-4">
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-medium text-gray-900">
                Edit {selectedCards.size} Card{selectedCards.size !== 1 ? 's' : ''} as JSON
              </h2>
              <button
                onClick={handleCancelJsonEdit}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Edit the JSON below. Each card must have: id, front, and back fields.
                </label>
                <textarea
                  value={jsonText}
                  onChange={(e) => setJsonText(e.target.value)}
                  rows={20}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 font-mono text-xs"
                />
              </div>

              {jsonError && (
                <div className="rounded-md bg-red-50 p-4">
                  <p className="text-sm text-red-800">{jsonError}</p>
                </div>
              )}

              <div className="flex justify-end space-x-2">
                <button
                  onClick={handleCancelJsonEdit}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (!selectedJsonFields.has('id')) {
                      alert('ID must be included to edit cards here.')
                      return
                    }
                    handleSaveJson()
                  }}
                  className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                    selectedJsonFields.has('id') 
                      ? 'bg-indigo-600 hover:bg-indigo-700' 
                      : 'bg-gray-400 cursor-not-allowed'
                  }`}
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Admin Controls */}
      {isAdmin && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-yellow-800">Admin Controls</h3>
              <p className="text-sm text-yellow-600 mt-1">Force all users to clear local data and resync</p>
            </div>
            <button
              onClick={handleForceResync}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
            >
              Force Client Resync
            </button>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end space-x-2">
        <button
          onClick={() => setIsCreating(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Create Card
        </button>
        <button
          onClick={() => setShowBatchAdd(true)}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Batch Add
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow space-y-4">
        <div>
          <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
            Search cards
          </label>
          <input
            id="search"
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search in front or back..."
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        </div>

        {availableExercises.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter by exercise
            </label>
            <div className="flex flex-wrap gap-2">
              {availableExercises.map(ex => (
                <button
                  key={ex.id}
                  onClick={() => handleExerciseToggle(ex.id)}
                  className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium ${
                    selectedExercises.includes(ex.id)
                      ? 'bg-purple-100 text-purple-800 border border-purple-300'
                      : 'bg-gray-50 text-gray-700 border border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  {ex.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {availableTags.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter by tags
            </label>
            <div className="flex flex-wrap gap-2">
              {availableTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => handleTagToggle(tag)}
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    selectedTags.includes(tag)
                      ? 'bg-indigo-100 text-indigo-800'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Bulk tag management for selected cards */}
        {selectedCards.size > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Manage tags for {selectedCards.size} selected card{selectedCards.size !== 1 ? 's' : ''}
            </label>
            <div className="flex flex-wrap gap-2 items-center">
              {/* Get common tags from selected cards */}
              {(() => {
                const selectedCardsList = Array.from(selectedCards)
                  .map(id => cards.find(c => c.id === id))
                  .filter(Boolean) as Card[]
                
                // Get all unique tags from selected cards
                const allTagsFromSelected = new Set<string>()
                selectedCardsList.forEach(card => {
                  card.tags.forEach(tag => allTagsFromSelected.add(tag))
                })
                
                return Array.from(allTagsFromSelected).sort().map(tag => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800 group cursor-pointer hover:bg-indigo-200"
                    onClick={() => handleBulkRemoveTag(tag)}
                  >
                    {tag}
                    <span className="ml-1 opacity-0 group-hover:opacity-100">×</span>
                  </span>
                ))
              })()}
              
              {!isBulkAddingTag ? (
                <button
                  onClick={() => setIsBulkAddingTag(true)}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-600 hover:bg-gray-200"
                >
                  + Add tag
                </button>
              ) : (
                <div className="flex items-center gap-1">
                  <input
                    type="text"
                    value={bulkTagInput}
                    onChange={(e) => setBulkTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleBulkAddTag()
                      } else if (e.key === 'Escape') {
                        setIsBulkAddingTag(false)
                        setBulkTagInput('')
                      }
                    }}
                    placeholder="New tag for all selected"
                    className="px-3 py-1 text-sm border border-gray-300 rounded-full"
                    autoFocus
                  />
                  <button
                    onClick={handleBulkAddTag}
                    className="text-sm text-green-600 hover:text-green-700"
                  >
                    ✓
                  </button>
                  <button
                    onClick={() => {
                      setIsBulkAddingTag(false)
                      setBulkTagInput('')
                    }}
                    className="text-sm text-red-600 hover:text-red-700"
                  >
                    ×
                  </button>
                </div>
              )}
              
              {/* Language dropdown */}
              <div className="ml-auto">
                <select
                  value={bulkLanguage || ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      handleBulkLanguageUpdate(e.target.value)
                    }
                  }}
                  className="text-sm rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                >
                  <option value="">Change language...</option>
                  <option value="en">English (EN)</option>
                  <option value="es">Spanish (ES)</option>
                  <option value="fr">French (FR)</option>
                  <option value="de">German (DE)</option>
                  <option value="it">Italian (IT)</option>
                  <option value="pt">Portuguese (PT)</option>
                  <option value="ja">Japanese (JA)</option>
                  <option value="ko">Korean (KO)</option>
                  <option value="zh">Chinese (ZH)</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={selectedCards.size === filteredCards.length && filteredCards.length > 0}
                onChange={handleSelectAll}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="ml-2 text-sm text-gray-700">
                Select all ({filteredCards.length})
              </span>
            </label>
            {selectedCards.size > 0 && (
              <span className="text-sm text-gray-500">
                {selectedCards.size} selected
              </span>
            )}
          </div>
          
          {selectedCards.size > 0 && (
            <div className="flex space-x-2">
              {/* Edit JSON with dropdown */}
              <div className="relative json-dropdown-container">
                <button
                  onClick={() => {
                    setShowEditJsonDropdown(!showEditJsonDropdown)
                    setShowExportJsonDropdown(false)
                  }}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Edit JSON
                  <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showEditJsonDropdown && (
                  <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                    <div className="py-1">
                      <div className="px-4 py-2 text-sm text-gray-700 font-medium">Select fields:</div>
                      {availableFields.map(field => (
                        <label key={field} className="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedJsonFields.has(field)}
                            onChange={() => toggleFieldSelection(field)}
                            className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">{field}</span>
                        </label>
                      ))}
                      <div className="border-t border-gray-100 px-4 py-2">
                        <button
                          onClick={() => {
                            handleEditJson()
                            setShowEditJsonDropdown(false)
                          }}
                          className="w-full text-center px-3 py-1 text-sm font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700"
                        >
                          Edit
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Export JSON with dropdown */}
              <div className="relative json-dropdown-container">
                <button
                  onClick={() => {
                    setShowExportJsonDropdown(!showExportJsonDropdown)
                    setShowEditJsonDropdown(false)
                  }}
                  className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Export JSON
                  <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showExportJsonDropdown && (
                  <div className="absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                    <div className="py-1">
                      <div className="px-4 py-2 text-sm text-gray-700 font-medium">Select fields:</div>
                      {availableFields.map(field => (
                        <label key={field} className="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedJsonFields.has(field)}
                            onChange={() => toggleFieldSelection(field)}
                            className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">{field}</span>
                        </label>
                      ))}
                      <div className="border-t border-gray-100 px-4 py-2">
                        <button
                          onClick={() => {
                            handleExportSelected()
                            setShowExportJsonDropdown(false)
                          }}
                          className="w-full text-center px-3 py-1 text-sm font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700"
                        >
                          Export
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={handleResetSelected}
                className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Reset Progress
              </button>
              <button
                onClick={handleDeleteSelected}
                className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
              >
                Delete Selected
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Cards List */}
      <div className="space-y-4">
        {filteredCards.length === 0 ? (
          <div className="text-center py-8 bg-white rounded-lg">
            <p className="text-gray-500">No cards match your filters.</p>
          </div>
        ) : (
          filteredCards.map((card) => (
            <div
              key={card.id}
              className={`bg-white shadow rounded-lg p-6 hover:shadow-md transition-shadow ${
                selectedCards.has(card.id) ? 'ring-2 ring-indigo-500' : ''
              }`}
            >
              <div className="flex items-start space-x-4">
                <input
                  type="checkbox"
                  checked={selectedCards.has(card.id)}
                  onChange={() => handleSelectCard(card.id)}
                  className="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                
                <div className="flex-1">
                  <div className="mb-4">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Front:</h3>
                    <p className="text-gray-700 whitespace-pre-wrap">{card.front}</p>
                  </div>
                  
                  <div className="mb-4">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Back:</h3>
                    <p className="text-gray-700 whitespace-pre-wrap">{card.back}</p>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex flex-col gap-2">
                      {/* Exercise display */}
                      {card.exercise_id && exerciseNameMap.get(card.exercise_id) && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Exercise:</span>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                            {exerciseNameMap.get(card.exercise_id)}
                          </span>
                        </div>
                      )}

                      {/* Tags */}
                      <div className="flex flex-wrap gap-2 items-center">
                        {card.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 group cursor-pointer hover:bg-indigo-200"
                            onClick={() => handleRemoveTag(card.id, tag)}
                          >
                            {tag}
                            <span className="ml-1 opacity-0 group-hover:opacity-100">×</span>
                          </span>
                        ))}
                      {editingTagCardId === card.id ? (
                        <div className="flex items-center gap-1">
                          <input
                            type="text"
                            value={newTagInput}
                            onChange={(e) => setNewTagInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault()
                                handleAddTag(card.id)
                              } else if (e.key === 'Escape') {
                                setEditingTagCardId(null)
                                setNewTagInput('')
                              }
                            }}
                            placeholder="New tag"
                            className="px-2 py-0.5 text-xs border border-gray-300 rounded"
                            autoFocus
                          />
                          <button
                            onClick={() => handleAddTag(card.id)}
                            className="text-xs text-green-600 hover:text-green-700"
                          >
                            ✓
                          </button>
                          <button
                            onClick={() => {
                              setEditingTagCardId(null)
                              setNewTagInput('')
                            }}
                            className="text-xs text-red-600 hover:text-red-700"
                          >
                            ×
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setEditingTagCardId(card.id)}
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200"
                        >
                          + Add tag
                        </button>
                      )}
                        <span className="text-xs text-gray-500 ml-2">
                          {card.language.toUpperCase()} {card.level && card.level}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleClone(card)}
                        className="text-sm text-gray-600 hover:text-gray-800"
                      >
                        Clone
                      </button>
                      <button
                        onClick={() => handleEdit(card)}
                        className="text-sm text-indigo-600 hover:text-indigo-500"
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}